'use client';

import React, { createContext, useContext, useCallback } from 'react';
import { toSlug } from '@framework/url/helpers/toSlug';
import { useRouter, getQueryString } from '@framework/router';
import { IAssetClass } from '@/creditflowresearch/domain/vo/IAssetClass';
import useAssetsClasses from '../BondsSummary/useAssetClasses';

export const useBondsAssetClasses = (initial?: IAssetClass[]) => {
  const {
    assetsClasses,
    currentAssetClass,
    setCurrentAssetClass,
  } = useAssetsClasses(initial);
  const { query, replace } : any = useRouter();
  const { assetClass: queryAssetClass, bondType, ...rest } = query;
  const search = getQueryString(rest);

  const handleAssetClassClick = useCallback((assetClass) => {
    const sluggedAssetClassAbbr = toSlug(assetClass.abbreviation);
    if (sluggedAssetClassAbbr !== assetClass) {
      setCurrentAssetClass(assetClass);
      replace(`/${ bondType }-${ toSlug(assetClass.abbreviation) }?${ search }`, undefined, { shallow: true });
    }
  }, [queryAssetClass, bondType, search]);

  return {
    assetsClasses,
    handleAssetClassClick,
    currentAssetClass,
    setCurrentAssetClass,
  };
};

type BondsAssetClassesContextType = ReturnType<typeof useBondsAssetClasses>;

export const BondsAssetClassesContext = createContext<BondsAssetClassesContextType>(null);

export const useBondsAssetClassesContext = () => {
  const context = useContext(BondsAssetClassesContext);
  if (!context) {
    throw new Error('useBondsAssetClassesContext must be used within the BondsAssetClassesContext');
  }
  return context;
};

interface IProps {
  initial?: IAssetClass[],
  children: React.ReactNode,
}

const BondsAssetClassesContextProvider = ({ initial, children }: IProps) => (
  <BondsAssetClassesContext.Provider value={ useBondsAssetClasses(initial) }>
    { children }
  </BondsAssetClassesContext.Provider>
);

export default BondsAssetClassesContextProvider;
